import { ChangeEvent, useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuditLogStore } from "@/store/audit-logs";
import { cn } from "@/lib/utils";
import { RefreshCw, RotateCcw, Search } from "lucide-react";
import { COLUMN_DEFINITIONS, ColumnKey } from "./audit-table";

interface AuditFiltersProps {
    className?: string;
    visibleColumns: Record<ColumnKey, boolean>;
    onToggleColumn: (key: ColumnKey, value: boolean) => void;
}

export const AuditFilters = ({
    className,
    visibleColumns,
    onToggleColumn,
}: AuditFiltersProps) => {
    const filters = useAuditLogStore((state) => state.filters);
    const setTargetId = useAuditLogStore((state) => state.setTargetId);
    const fetchEntries = useAuditLogStore((state) => state.fetchEntries);
    const resetFilters = useAuditLogStore((state) => state.resetFilters);
    const loading = useAuditLogStore((state) => state.loading);
    const [viewSelectValue, setViewSelectValue] = useState<string>("");

    const handleTargetIdChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setTargetId(event.target.value);
        },
        [setTargetId],
    );

    const handleViewChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) => {
            const value = event.target.value;
            setViewSelectValue(value);

            if (!value) {
                return;
            }

            if (value === "showAll") {
                COLUMN_DEFINITIONS.forEach((column) => {
                    if (!visibleColumns[column.key]) {
                        onToggleColumn(column.key, true);
                    }
                });
            } else if (value.startsWith("toggle:")) {
                const columnKey = value.replace("toggle:", "") as ColumnKey;
                const current = visibleColumns[columnKey];
                onToggleColumn(columnKey, !current);
            }

            setViewSelectValue("");
        },
        [onToggleColumn, visibleColumns],
    );

    const handleManualRefresh = useCallback(async () => {
        await fetchEntries("current");
    }, [fetchEntries]);

    const handleResetFilters = useCallback(async () => {
        resetFilters();
        await fetchEntries("initial");
    }, [resetFilters, fetchEntries]);

    return (
        <div
            className={cn(
                "flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm md:flex-row md:items-center md:justify-between",
                className,
            )}
        >
            <div className="flex w-full flex-1 flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="w-full bg-input/80 pl-10"
                        placeholder="Filter by target or Evalion ID"
                        value={filters.targetId}
                        onChange={handleTargetIdChange}
                    />
                </div>
                <select
                    className="h-10 min-w-[160px] rounded-md border border-input bg-input/60 px-3 text-sm text-secondary-text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={viewSelectValue}
                    onChange={handleViewChange}
                >
                    <option value="">View: choose columns</option>
                    <option value="showAll">Show all columns</option>
                    {COLUMN_DEFINITIONS.map((column) => (
                        <option key={column.key} value={`toggle:${column.key}`}>
                            {visibleColumns[column.key] ? `Hide ${column.label}` : `Show ${column.label}`}
                        </option>
                    ))}
                </select>
            </div>
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
                <Button
                    variant="outline"
                    onClick={handleResetFilters}
                    disabled={loading}
                    className="w-full md:w-auto"
                >
                    <RotateCcw className={cn("mr-2 h-4 w-4", loading && "animate-spin")}
                    />
                    Reset
                </Button>
                <Button
                    variant="outline"
                    onClick={handleManualRefresh}
                    disabled={loading}
                    className="w-full md:w-auto"
                >
                    <RefreshCw
                        className={cn("mr-2 h-4 w-4", loading && "animate-spin")}
                    />
                    Refresh
                </Button>
            </div>
        </div>
    );
};

